import { useEffect, useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router";
import { ApiError } from "../data/api.js";

// Pauza (CR-013): odkaz na pokračování e-mailem. E-mail se nikam neukládá (ani v zařízení),
// formulář je jen online a až po odeslání fronty odpovědí. „Bez e-mailu“ vrátí domů, na stejném
// zařízení jde pokračovat jako dosud. „Zpět do testu“ (i klávesa Escape) vrátí hned na tutéž
// obrazovku testu (Libor 19. 10. 2026).

type State =
  | { kind: "preparing" }
  | { kind: "form"; error: "invalid" | "failed" | "limit" | null; sending: boolean }
  | { kind: "local"; offline: boolean }
  | { kind: "sent" }
  | { kind: "dev"; link: string };

export interface PauseDialogProps {
  /** Odešle frontu odpovědí; `false` znamená bez připojení. */
  readonly flush: () => Promise<boolean>;
  readonly send: (email: string) => Promise<{ sent: boolean; dev_link?: string }>;
  readonly onHome: () => void;
  /** Návrat na tutéž obrazovku testu. */
  readonly onBack: () => void;
}

export function PauseDialog({ flush, send, onHome, onBack }: PauseDialogProps) {
  const intl = useIntl();
  const [state, setState] = useState<State>({ kind: "preparing" });
  const [email, setEmail] = useState("");

  useEffect(() => {
    let live = true;
    void flush().then((online) => {
      if (live) setState(online ? { kind: "form", error: null, sending: false } : { kind: "local", offline: true });
    });
    return () => {
      live = false;
    };
  }, [flush]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onBack();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (state.kind !== "form" || state.sending) return;
    setState({ kind: "form", error: null, sending: true });
    try {
      const res = await send(email.trim());
      setEmail("");
      setState(res.dev_link !== undefined ? { kind: "dev", link: res.dev_link } : { kind: "sent" });
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 0) setState({ kind: "local", offline: true });
      else setState({ kind: "form", sending: false, error: status === 400 ? "invalid" : status === 429 ? "limit" : "failed" });
    }
  }

  return (
    <section className="card stack pause" aria-labelledby="pause-title" data-testid="pause">
      <button type="button" className="pause__back" onClick={onBack} data-testid="pause-back">
        <span aria-hidden="true">← </span>
        <FormattedMessage id="test.pause.back" />
      </button>
      <h2 id="pause-title">
        <FormattedMessage id="test.pause.title" />
      </h2>
      {state.kind === "form" ? (
        <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
          <p>
            <FormattedMessage id="test.pause.lead" />
          </p>
          <label className="field">
            <span className="label">
              <FormattedMessage id="test.pause.email" />
            </span>
            <input
              className="share-url"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              aria-invalid={state.error === "invalid"}
            />
          </label>
          <p className="small muted">
            <FormattedMessage id="test.pause.privacy" />{" "}
            <Link to={`/${intl.locale}/privacy`}>
              <FormattedMessage id="test.pause.privacyLink" />
            </Link>
          </p>
          {state.error === null ? null : (
            <p className="small" role="alert" data-testid="pause-error">
              <FormattedMessage id={`test.pause.${state.error === "failed" ? "error" : state.error}`} />
            </p>
          )}
          <button type="submit" className="button" disabled={state.sending || email.trim() === ""} data-testid="pause-send">
            <FormattedMessage id="test.pause.send" />
          </button>
        </form>
      ) : null}
      {state.kind === "local" ? (
        <p>
          <FormattedMessage id={state.offline ? "test.pause.offline" : "test.pause.local"} />
        </p>
      ) : null}
      {state.kind === "sent" ? (
        <p role="status" data-testid="pause-sent">
          <FormattedMessage id="test.pause.sent" />
        </p>
      ) : null}
      {state.kind === "dev" ? (
        <div className="stack" role="status">
          <p className="small muted">
            <FormattedMessage id="test.pause.devLink" />
          </p>
          <a href={state.link} data-testid="pause-dev-link">
            {state.link}
          </a>
        </div>
      ) : null}
      {state.kind === "preparing" ? null : (
        <button type="button" className="link-button" onClick={onHome} data-testid="pause-home">
          <FormattedMessage id={state.kind === "form" || state.kind === "local" ? "test.pause.noEmail" : "claim.home"} />
        </button>
      )}
    </section>
  );
}
